import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { getPushRegistration } from "@/lib/pushRegistration";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, Send, Mail, Webhook, RefreshCw, Smartphone } from "lucide-react";

// Group label → display name
const GROUP_LABELS = {
  TRADE:   "Trade Events",
  BROKER:  "Broker",
  RISK:    "Risk & System",
  PRICE:   "Price Alerts",
  ACCOUNT: "Account",
};

export default function AlertsPanel() {
  const [prefs, setPrefs]         = useState(null);
  const [transports, setTransports] = useState({});
  const [categories, setCategories] = useState({});
  const [log, setLog]             = useState([]);
  const [busy, setBusy]           = useState(false);
  const [pushCount, setPushCount] = useState(0);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPerm, setPushPerm]   = useState("default"); // default | granted | denied
  const swRef = useRef(null);

  // Detect push API support on mount
  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      setPushSupported(true);
      setPushPerm(Notification.permission);
    }
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const [p, l] = await Promise.all([
        api.get("/alerts/prefs"),
        api.get("/alerts/log", { params: { limit: 25 } }),
      ]);
      setPrefs(p.data.prefs);
      setTransports(p.data.transports || {});
      setCategories(p.data.event_categories || {});
      setPushCount(p.data.push_subscription_count || 0);
      setLog(l.data.items || []);
    } catch {
      toast.error("Could not load alert settings");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!prefs) return;
    setBusy(true);
    try {
      await api.put("/alerts/prefs", prefs);
      toast.success("Alert preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async (channel) => {
    setBusy(true);
    try {
      await api.put("/alerts/prefs", prefs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed before test");
      setBusy(false);
      return;
    }
    try {
      await api.post("/alerts/test", { channel });
      toast.success(`Test ${channel} alert dispatched`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${channel} test failed`);
    } finally {
      setBusy(false);
    }
  };

  const toggleEventType = (et) => {
    const cur = new Set(prefs.event_types || []);
    if (cur.has(et)) cur.delete(et); else cur.add(et);
    setPrefs({ ...prefs, event_types: Array.from(cur) });
  };

  // Subscribe this browser to Web Push
  const subscribePush = async () => {
    if (!pushSupported) return;
    try {
      // Get VAPID public key
      const { data } = await api.get("/alerts/vapid-public-key");
      const publicKey = data.public_key;

      // RU-PREVIEWGATE2 (PG2-12): use the app's service worker at scope "/" (it carries the push handlers)
      const reg = await getPushRegistration(navigator.serviceWorker);
      swRef.current = reg;

      // Request permission
      const perm = await Notification.requestPermission();
      setPushPerm(perm);
      if (perm !== "granted") {
        toast.error("Push permission denied — allow notifications in browser settings");
        return;
      }

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: _urlBase64ToUint8Array(publicKey),
      });

      await api.post("/alerts/push-subscribe", { subscription: sub.toJSON() });
      setPrefs((p) => ({ ...p, push_enabled: true }));
      setPushCount((c) => c + 1);
      toast.success("Push notifications enabled for this device");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Push subscription failed");
    }
  };

  const unsubscribePush = async () => {
    try {
      await api.delete("/alerts/push-unsubscribe");
      setPrefs((p) => ({ ...p, push_enabled: false }));
      setPushCount(0);
      toast.success("Push notifications removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unsubscribe failed");
    }
  };

  if (!prefs) return null;

  const tgConfigured = transports.telegram === "configured";
  const emConfigured = transports.email    === "configured";
  const pushConfigured = transports.push   === "configured";

  return (
    <div className="space-y-6" data-testid="alerts-panel">

      {/* ── Transports ──────────────────────────────────────────────────────── */}
      <div className="panel p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 txt-warn" />
            <div className="overline">Notification channels</div>
          </div>
          <Button size="sm" variant="ghost" onClick={load} disabled={busy} className="rounded-none">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> RELOAD
          </Button>
        </div>
        <p className="txt-secondary text-sm mb-5">
          Choose your notification channels and select exactly which events you want to receive. Each channel can be tested independently.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

          {/* Telegram */}
          <div className="border border-[var(--border)] p-4 bg-[var(--bg-page)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 txt-muted" />
                <div className="font-section text-sm tracking-wider">TELEGRAM</div>
                <span className={`text-xs font-mono-data ${tgConfigured ? "txt-profit" : "txt-loss"}`}>
                  {tgConfigured ? "● READY" : "● NO TOKEN"}
                </span>
              </div>
              <Switch
                data-testid="alerts-telegram-enabled"
                checked={!!prefs.telegram_enabled}
                onCheckedChange={(v) => setPrefs({ ...prefs, telegram_enabled: v })}
              />
            </div>
            <Label className="overline">Your chat_id</Label>
            <Input
              data-testid="alerts-telegram-chat-id"
              value={prefs.telegram_chat_id || ""}
              onChange={(e) => setPrefs({ ...prefs, telegram_chat_id: e.target.value })}
              placeholder="e.g. 1234567890"
              className="mt-2 rounded-none bg-[var(--bg-surface)] border-[var(--border)] font-mono-data"
            />
            <p className="text-xs txt-muted mt-2">DM the platform bot and send <code>/start</code> to get your chat id.</p>
            <Button size="sm" variant="ghost" onClick={() => sendTest("telegram")}
              disabled={busy || !tgConfigured || !prefs.telegram_chat_id}
              className="mt-3 rounded-none border border-[var(--border)] font-section tracking-wider w-full">
              SEND TEST
            </Button>
          </div>

          {/* Email */}
          <div className="border border-[var(--border)] p-4 bg-[var(--bg-page)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 txt-muted" />
                <div className="font-section text-sm tracking-wider">EMAIL</div>
                <span className={`text-xs font-mono-data ${emConfigured ? "txt-profit" : "txt-loss"}`}>
                  {emConfigured ? "● SMTP READY" : "● NO SMTP"}
                </span>
              </div>
              <Switch
                data-testid="alerts-email-enabled"
                checked={!!prefs.email_enabled}
                onCheckedChange={(v) => setPrefs({ ...prefs, email_enabled: v })}
              />
            </div>
            <Label className="overline">Your email</Label>
            <Input
              data-testid="alerts-email-address"
              value={prefs.email_address || ""}
              onChange={(e) => setPrefs({ ...prefs, email_address: e.target.value })}
              placeholder="trader@example.com"
              className="mt-2 rounded-none bg-[var(--bg-surface)] border-[var(--border)] font-mono-data"
            />
            <p className="text-xs txt-muted mt-2">Plain-text via platform SMTP. Configure SMTP_* env vars to enable.</p>
            <Button size="sm" variant="ghost" onClick={() => sendTest("email")}
              disabled={busy || !emConfigured || !prefs.email_address}
              className="mt-3 rounded-none border border-[var(--border)] font-section tracking-wider w-full">
              SEND TEST
            </Button>
          </div>

          {/* Web Push */}
          <div className="border border-[var(--border)] p-4 bg-[var(--bg-page)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 txt-muted" />
                <div className="font-section text-sm tracking-wider">PUSH</div>
                <span className={`text-xs font-mono-data ${pushConfigured && pushSupported ? "txt-profit" : "txt-loss"}`}>
                  {!pushSupported ? "● NOT SUPPORTED" : pushConfigured ? "● VAPID READY" : "● NO VAPID KEY"}
                </span>
              </div>
              <Switch
                checked={!!prefs.push_enabled}
                disabled={!pushConfigured || !pushSupported || pushCount === 0}
                onCheckedChange={(v) => setPrefs({ ...prefs, push_enabled: v })}
              />
            </div>
            {pushCount > 0 ? (
              <p className="text-xs txt-profit mt-1 mb-2">
                ● {pushCount} device{pushCount !== 1 ? "s" : ""} subscribed
              </p>
            ) : (
              <p className="text-xs txt-muted mt-1 mb-2">No devices subscribed yet.</p>
            )}
            {pushPerm === "denied" && (
              <p className="text-xs txt-loss mb-2">Blocked by browser — allow in site settings.</p>
            )}
            <div className="flex flex-col gap-2 mt-2">
              <Button size="sm" variant="ghost" onClick={subscribePush}
                disabled={busy || !pushConfigured || !pushSupported || pushPerm === "denied"}
                className="rounded-none border border-[var(--border)] font-section tracking-wider">
                {pushCount > 0 ? "ADD THIS DEVICE" : "ENABLE PUSH"}
              </Button>
              {pushCount > 0 && (
                <Button size="sm" variant="ghost" onClick={unsubscribePush}
                  disabled={busy}
                  className="rounded-none border border-red-500/40 text-red-400 font-section tracking-wider">
                  REMOVE ALL
                </Button>
              )}
              {pushCount > 0 && (
                <Button size="sm" variant="ghost" onClick={() => sendTest("push")}
                  disabled={busy || !pushConfigured}
                  className="rounded-none border border-[var(--border)] font-section tracking-wider">
                  SEND TEST
                </Button>
              )}
            </div>
            <p className="text-xs txt-muted mt-3">Browser push — works even when the tab is closed.</p>
          </div>

          {/* Webhook */}
          <div className="border border-[var(--border)] p-4 bg-[var(--bg-page)]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Webhook className="w-4 h-4 txt-muted" />
                <div className="font-section text-sm tracking-wider">WEBHOOK</div>
                <span className="text-xs font-mono-data txt-profit">● ALWAYS AVAILABLE</span>
              </div>
              <Switch
                data-testid="alerts-webhook-enabled"
                checked={!!prefs.webhook_enabled}
                onCheckedChange={(v) => setPrefs({ ...prefs, webhook_enabled: v })}
              />
            </div>
            <Label className="overline">Endpoint URL</Label>
            <Input
              data-testid="alerts-webhook-url"
              value={prefs.webhook_url || ""}
              onChange={(e) => setPrefs({ ...prefs, webhook_url: e.target.value })}
              placeholder="https://your-service.com/hook"
              className="mt-2 rounded-none bg-[var(--bg-surface)] border-[var(--border)] font-mono-data"
            />
            <p className="text-xs txt-muted mt-2">POST JSON on every matching event. Retries once on failure.</p>
            <Button size="sm" variant="ghost" onClick={() => sendTest("webhook")}
              disabled={busy || !prefs.webhook_url}
              className="mt-3 rounded-none border border-[var(--border)] font-section tracking-wider w-full">
              SEND TEST
            </Button>
          </div>
        </div>

        {/* ── Event subscriptions ─────────────────────────────────────────── */}
        <div className="mt-8">
          <div className="overline mb-1">Event subscriptions</div>
          <p className="text-xs txt-muted mb-4">Select which events you want to receive. Active events are highlighted.</p>
          <div className="space-y-4">
            {Object.entries(categories).map(([group, events]) => (
              <div key={group}>
                <div className="text-xs font-section tracking-widest txt-muted mb-2">
                  {GROUP_LABELS[group] || group}
                </div>
                <div className="flex flex-wrap gap-2">
                  {events.map((et) => {
                    const active = (prefs.event_types || []).includes(et.id);
                    return (
                      <button
                        key={et.id}
                        data-testid={`alerts-evt-${et.id}`}
                        onClick={() => toggleEventType(et.id)}
                        title={et.desc}
                        className={`px-3 py-1.5 text-xs font-section tracking-wider border transition-colors ${
                          active
                            ? "bg-white text-black border-white"
                            : "bg-transparent text-zinc-400 border-[var(--border)] hover:text-white hover:border-zinc-500"
                        }`}
                      >
                        {et.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Button
          data-testid="alerts-save"
          onClick={save}
          disabled={busy}
          className="mt-8 rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
        >
          {busy ? "SAVING…" : "SAVE ALERT PREFERENCES"}
        </Button>
      </div>

      {/* ── Delivery log ────────────────────────────────────────────────────── */}
      <div className="panel p-6">
        <div className="overline mb-3">Recent alert deliveries</div>
        {log.length === 0 && <div className="txt-muted text-sm">No alerts dispatched yet.</div>}
        <div className="space-y-2">
          {log.map((row, i) => (
            <div
              key={`${row.ts}-${i}`}
              data-testid={`alerts-log-row-${i}`}
              className="flex items-center justify-between text-xs font-mono-data border-b border-[var(--border)] pb-2 gap-2"
            >
              <span className="txt-secondary shrink-0">{row.ts?.slice(0, 16)}</span>
              <span className="font-section tracking-wider shrink-0">{row.channel?.toUpperCase()}</span>
              <span className="txt-muted truncate flex-1">{row.destination}</span>
              <span className="font-section shrink-0">{row.event_type}</span>
              <span className={`shrink-0 ${row.ok ? "txt-profit" : "txt-loss"}`}>{row.ok ? "OK" : "FAIL"}</span>
              {!row.ok && row.error && (
                <span className="txt-loss truncate max-w-[160px]" title={row.error}>{row.error}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Convert VAPID URL-safe base64 to Uint8Array for browser pushManager
function _urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}
